import * as THREE from 'three';
import { TrackElement } from './TrackElement';
import { TrainingTrackElement, PowerUpType } from '../../../../../shared/src/trainingTrack.types';
import { TRAINING_TRACK_CONFIG } from '../config/TrainingTrackConfig';

/**
 * Class representing a power-up element in the training track
 */
export class PowerUpElement extends TrackElement {
    private powerUpMesh: THREE.Mesh | null = null;
    private powerUpType: string;
    private duration: number;
    private collected: boolean = false;
    
    /**
     * Constructor
     * @param element The power-up element data
     */
    constructor(element: TrainingTrackElement) {
        super(element);
        this.powerUpType = element.properties?.powerUpType || PowerUpType.SPEED_BOOST;
        this.duration = element.properties?.duration || 10.0;
    }
    
    /**
     * Creates the Three.js object for this power-up
     * @returns The created power-up object
     */
    protected createObject(): THREE.Object3D {
        // Choose color based on power-up type
        let color;
        switch (this.powerUpType) {
            case PowerUpType.SHIELD:
                color = TRAINING_TRACK_CONFIG.POWER_UP.COLORS.SHIELD;
                break;
            case PowerUpType.RAPID_FIRE:
                color = TRAINING_TRACK_CONFIG.POWER_UP.COLORS.RAPID_FIRE;
                break;
            case PowerUpType.HEALTH_BOOST:
                color = TRAINING_TRACK_CONFIG.POWER_UP.COLORS.HEALTH_BOOST;
                break;
            default:
                color = TRAINING_TRACK_CONFIG.POWER_UP.COLORS.SPEED_BOOST;
        }
        
        // Create a dodecahedron for the power-up
        const geometry = new THREE.DodecahedronGeometry(this.element.scale, 0);
        const material = new THREE.MeshStandardMaterial({
            color,
            emissive: color,
            emissiveIntensity: 0.5,
            transparent: true,
            opacity: 0.8
        });
        
        this.powerUpMesh = new THREE.Mesh(geometry, material);
        this.powerUpMesh.castShadow = true;
        
        // Position the power-up to hover above the ground
        this.powerUpMesh.position.y = TRAINING_TRACK_CONFIG.POWER_UP.HOVER_HEIGHT;
        
        return this.powerUpMesh;
    }
    
    /**
     * Updates the power-up animation
     * @param deltaTime Time elapsed since last update in seconds
     */
    public update(deltaTime: number): void {
        if (!this.powerUpMesh || this.collected) return;
        
        // Rotate the power-up
        this.powerUpMesh.rotation.y += TRAINING_TRACK_CONFIG.POWER_UP.ROTATION_SPEED * deltaTime * 10;
        
        // Make the power-up hover up and down
        const hoverOffset = Math.sin(Date.now() * TRAINING_TRACK_CONFIG.POWER_UP.HOVER_SPEED) * TRAINING_TRACK_CONFIG.POWER_UP.HOVER_HEIGHT / 2;
        this.powerUpMesh.position.y = TRAINING_TRACK_CONFIG.POWER_UP.HOVER_HEIGHT + hoverOffset;
    }
    
    /**
     * Handles player interaction with this power-up
     * @param playerPosition The player's position
     * @param playerTank The player's tank
     * @param distance The distance to the player
     * @returns True if interaction was handled, false otherwise
     */
    public handleInteraction(playerPosition: THREE.Vector3, playerTank: any, distance: number): boolean {
        if (this.collected || !this.powerUpMesh) return false;
        
        // Check if player is close enough to collect the power-up
        if (distance < this.element.scale * 2) {
            // Mark as collected and hide
            this.collected = true;
            this.powerUpMesh.visible = false;
            
            // Apply power-up effect to player's tank if it has the applyPowerUp method
            if (playerTank.applyPowerUp) {
                playerTank.applyPowerUp(this.powerUpType, this.duration * 1000);
            }
            
            // Respawn power-up after a delay
            setTimeout(() => {
                this.collected = false;
                if (this.powerUpMesh) {
                    this.powerUpMesh.visible = true;
                }
            }, this.duration * 1000 + 5000); // Respawn 5 seconds after effect ends
            
            return true;
        }
        
        return false;
    }
    
    /**
     * Gets the power-up type
     * @returns The power-up type
     */
    public getPowerUpType(): string {
        return this.powerUpType;
    }
    
    /**
     * Gets the power-up duration
     * @returns The power-up duration in seconds
     */
    public getDuration(): number {
        return this.duration;
    }
    
    /**
     * Checks if the power-up has been collected
     * @returns True if the power-up has been collected, false otherwise
     */
    public isCollected(): boolean {
        return this.collected;
    }
}